import React, { useState } from 'react';
import { Tag, Form, Card, Steps, Progress } from 'antd';
import { round, toLower, uniqueId } from 'lodash';
import TableComponent from '@/components/Table';
import InputComponent from '@/components/Input';
import ButtonComponent from '@/components/Button';
import SelectInput from '@/components/Select';
import {
  PAGE_SIZE,
  SEND_USING_FILES_SEND_TYPES,
  SMS_STATUSES,
} from '@/constants';
import ModalComponent from '@/components/Modal';
import { useTranslation } from 'react-i18next';
import { API } from '@/constants/api';
import { ReactComponent as SearchIcon } from '@/assets/images/icons/search.svg';
import { ReactComponent as PlusIcon } from '@/assets/images/icons/plus.svg';
import { ReactComponent as UploadIcon } from '@/assets/images/icons/upload.svg';
import { ReactComponent as TableIcon } from '@/assets/images/icons/table.svg';
import { ReactComponent as CheckCircleWhiteIcon } from '@/assets/images/icons/check-circle-white.svg';
import { ReactComponent as DownloadIcon } from '@/assets/images/icons/download.svg';
import { SmsManagementService } from '@/services';
import { parseDateTime } from '@/utils/formatter';
import EllipsisTooltip from '@/components/EllipsisTooltip';
import SelectFetching from '@/components/SelectFetching';
import { validateScheduleCampaignTime } from '@/utils/validate';
import { getCurrentCountry } from '@/utils/domain';
import { shouldHideSender } from '@/utils/hideSender';
import ImportFile from './Steps/ImportFile';
import ValidateData from './Steps/ValidateData';
import Finish from './Steps/Finish';

import './send-using-file.scss';

const colSize = 100 / 8;
function SendUsingFile() {
  const [filterForm] = Form.useForm();
  const [filterParams, setFilterParams] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loadingValidateData, setLoadingValidateData] = useState(true);
  const [batchFormData, setBatchFormData] = useState({
    description: null,
    senderId: null,
    templateId: null,
    file: null,
    filePath: null,
    scheduleAt: null,
    sendType: SEND_USING_FILES_SEND_TYPES.sendNow,
  });
  const [validatedData, setValidatedData] = useState({
    validRowCount: null,
    totalSmsCredit: null,
    invalidRowCount: null,
    errorFile: null,
  });
  const [requestId, setRequestId] = useState(null);
  const [triggerRefreshTable, setTriggerRefreshTable] = useState(false);
  const [needFetchBatch, setNeedFetchBatch] = useState(false);
  const [triggerValidateImportFileForm, setTriggerValidateImportFileForm] =
    useState(false);

  const { t } = useTranslation();

  // get the current Country
  const country = getCurrentCountry();

  const columns = [
    {
      title: t('sms_sending_requests.request_id'),
      dataIndex: 'id',
      width: `${1.2 * colSize}%`,
    },
    {
      title: t('sms_create_new_request.description'),
      dataIndex: 'description',
      ellipsis: {
        showTitle: false,
      },
      render: (_, { description }) => (
        <EllipsisTooltip title={description}>{description}</EllipsisTooltip>
      ),
    },
    ...(shouldHideSender(country)
      ? []
      : [
          {
            title: t('sms_page.tabs.third'),
            dataIndex: 'senderName',
            ellipsis: {
              showTitle: false,
            },
            render: (_, { senderName: _senderName }) => (
              <EllipsisTooltip title={_senderName}>
                {_senderName}
              </EllipsisTooltip>
            ),
          },
        ]),
    {
      title: t('transactions_page.detail.additional_information.created_by'),
      dataIndex: 'createdby',
      width: `${1.5 * colSize}%`,
      render: (_, { createdBy, createdAt }) => (
        <div className="created-by-container">
          {createdBy && <div className="created-by">{createdBy}</div>}
          {createdAt && (
            <div className="created-at">{parseDateTime(createdAt)}</div>
          )}
        </div>
      ),
    },
    {
      title: t(
        'sms_page.send_using_file.create.validate_data.estimated_sms_credit',
      ),
      dataIndex: 'estimatedTotalCredit',
      render: (_, { estimatedTotalCredit }) =>
        estimatedTotalCredit.toLocaleString(),
    },
    {
      title: t('sms_create_new_request.total_sms_credit_spent'),
      dataIndex: 'actualTotalCredit',
      render: (_, { actualTotalCredit }) => actualTotalCredit.toLocaleString(),
    },
    {
      title: t('sms_template_list.table.schedule_campaign_time'),
      dataIndex: 'scheduleAt',
      width: `${0.9 * colSize}%`,
      render: (_, { scheduleAt }) => {
        if (!scheduleAt) {
          return null;
        }
        return parseDateTime(scheduleAt, 'DD/MM/YYYY HH:mm');
      },
    },
    {
      title: t('sms_sending_requests.status.progress'),
      dataIndex: 'progress',
      render: (_, { status, progress }) => {
        const statusText = SMS_STATUSES.find(
          (item) => item.value === status,
        )?.label;
        return (
          <Progress
            key={uniqueId()}
            percent={round(progress)}
            className={`sms-progress-${toLower(statusText)}`}
          />
        );
      },
    },
    {
      title: t('common.transactions_table.form_filter.status.label'),
      dataIndex: 'status',
      width: '140px',
      render: (_, { status }) => {
        const statusText = SMS_STATUSES.find(
          (item) => item.value === status,
        )?.label;
        return (
          <Tag
            key={statusText}
            className={`sms-tag sms-${toLower(statusText)}`}
          >
            <span>{t(`common.constants.sms_statuses.${statusText}`)}</span>
          </Tag>
        );
      },
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: '80px',
      align: 'center',
      render: (_, { reportFile }) => {
        if (!reportFile) {
          return null;
        }

        const fileName = new URL(reportFile).pathname.split('/').pop();
        return (
          <a
            href={reportFile}
            download={fileName}
            target="_blank"
            rel="noreferrer"
          >
            <DownloadIcon />
          </a>
        );
      },
    },
  ];

  const fetchSenderList = async (name) => {
    const res = await SmsManagementService.fetchActiveSenderList({ name });

    if (res.success) {
      return res.data.map((sender) => ({
        label: sender.senderName,
        value: sender.senderName,
      }));
    }
    return [];
  };

  const handleOnFormFilterFinish = (values) => {
    setTriggerRefreshTable(true);
    const { senderName } = values;
    if (senderName) {
      values.senderName = senderName.value;
    }
    setFilterParams({
      ...filterParams,
      ...values,
      pagination: {
        current: 1,
        pageSize: PAGE_SIZE,
      },
    });

    setTimeout(() => {
      setTriggerRefreshTable(false);
    }, 50);
  };

  const afterCloseModal = () => {
    setLoadingValidateData(true);
    setCurrentStep(0);
    setBatchFormData({
      description: null,
      senderId: null,
      templateId: null,
      file: null,
      filePath: null,
      scheduleAt: null,
      sendType: SEND_USING_FILES_SEND_TYPES.sendNow,
    });
    setValidatedData({
      validRowCount: null,
      totalSmsCredit: null,
      invalidRowCount: null,
      errorFile: null,
    });

    if (needFetchBatch) {
      setTriggerRefreshTable(true);
    }
    setNeedFetchBatch(false);
    setTriggerValidateImportFileForm(false);
  };

  const handleOnValidateData = async (values) => {
    setCurrentStep(currentStep + 1);
    const {
      description,
      senderId,
      templateId,
      fileS3Path,
      file,
      scheduleAt,
      sendType,
    } = values;

    setBatchFormData({
      ...batchFormData,
      description,
      templateId,
      file,
      senderId,
      filePath: fileS3Path,
      scheduleAt,
      sendType,
    });

    const { success, data: dataResp } = await SmsManagementService.validateData(
      {
        filePath: fileS3Path,
        templateId,
        senderId: senderId?.value,
      },
    );

    if (success) {
      setValidatedData(dataResp);
    }

    setLoadingValidateData(false);
  };

  const handleCreateBatch = async () => {
    const { description, senderId, templateId, filePath, scheduleAt } =
      batchFormData;
    if (scheduleAt && !validateScheduleCampaignTime(scheduleAt)) {
      setCurrentStep(currentStep - 1);
      setTriggerValidateImportFileForm(true);
      return;
    }
    const { success, data: dataResp } = await SmsManagementService.createBatch({
      filePath,
      templateId,
      senderId: senderId.value,
      description,
      scheduleAt,
    });

    if (success) {
      setRequestId(dataResp.id);
      setCurrentStep(currentStep + 1);
      setNeedFetchBatch(true);
    }
  };

  const handleOnBack = () => {
    setCurrentStep(currentStep - 1);
    setLoadingValidateData(true);
    setTriggerValidateImportFileForm(false);
  };

  const handleOnNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleOnClickAddNew = () => {
    setModalOpen(true);
    setTriggerRefreshTable(false);
  };

  const smsStatusOptions = SMS_STATUSES.map((status) => ({
    label: t(`common.constants.sms_statuses_lower.${status.label}`),
    value: status.value,
  }));

  return (
    <div className="send-using-file-container">
      <Card
        title={t('sms_sending_requests.sms_sending_request')}
        bordered={false}
        extra={
          <ButtonComponent
            type="primary"
            icon={<PlusIcon />}
            className="btn-add-new"
            onClick={handleOnClickAddNew}
          >
            {t('sms_sending_requests.create_new_request')}
          </ButtonComponent>
        }
      >
        <div className="form-filter">
          <Form
            name="filter"
            layout="vertical"
            onFinish={handleOnFormFilterFinish}
            autoComplete="off"
            className="form"
            form={filterForm}
          >
            <Form.Item
              label={t('sms_sending_requests.request_id')}
              name="Id"
              className="form-item"
            >
              <InputComponent
                placeholder={t('sms_sending_requests.request_id')}
              />
            </Form.Item>
            <Form.Item
              hidden={shouldHideSender(country)}
              label={t('sms_sending_requests.sender_name')}
              name="senderName"
              className="form-item"
            >
              <SelectFetching
                allowClear
                triggerFetching
                placeholder={t('sms_sending_requests.sender_name')}
                fetchOptions={fetchSenderList}
              />
            </Form.Item>
            <Form.Item
              label={t('sms_sending_requests.status_search')}
              name="status"
              className="form-item"
            >
              <SelectInput
                allowClear
                placeholder={t('sms_sending_requests.status_search')}
                options={smsStatusOptions}
              />
            </Form.Item>
            <Form.Item>
              <div className="form-actions-item">
                <ButtonComponent
                  type="primary"
                  htmlType="submit"
                  icon={<SearchIcon />}
                >
                  {t('transactions_page_trans.search')}
                </ButtonComponent>
                <ButtonComponent
                  type="default"
                  onClick={() => {
                    filterForm.resetFields();
                  }}
                >
                  {t('transactions_page_trans.reset')}
                </ButtonComponent>
              </div>
            </Form.Item>
          </Form>
        </div>

        <TableComponent
          columns={columns}
          filterParams={filterParams}
          triggerRefresh={triggerRefreshTable}
          action={API.SMS_MANAGERMENT.SEND_USING_FILE.INDEX}
          scroll={{
            scrollToFirstRowOnChange: true,
            x: 1440,
          }}
        />
      </Card>

      <ModalComponent
        title={t('sms_page.tabs.second')}
        centered
        open={modalOpen}
        footer={null}
        onCancel={() => setModalOpen(false)}
        afterClose={afterCloseModal}
      >
        <>
          <Steps
            current={currentStep}
            labelPlacement="vertical"
            items={[
              {
                title: t('sms_create_new_request.import_file'),
                icon: <UploadIcon />,
              },
              {
                title: t('sms_create_new_request.validate_data'),
                icon: <TableIcon />,
              },
              {
                title: t('sms_create_new_request.successful'),
                icon: <CheckCircleWhiteIcon />,
              },
            ]}
          />
          <div className="steps-content">
            {currentStep === 0 && (
              <ImportFile
                data={batchFormData}
                onSubmit={handleOnValidateData}
                triggerValidateForm={triggerValidateImportFileForm}
              />
            )}
            {currentStep === 1 && (
              <ValidateData
                loading={loadingValidateData}
                data={validatedData}
                onBack={handleOnBack}
                onNext={handleOnNext}
                onSubmit={handleCreateBatch}
              />
            )}

            {currentStep === 2 && (
              <Finish requestId={requestId} setModalOpen={setModalOpen} />
            )}
          </div>
        </>
      </ModalComponent>
    </div>
  );
}

SendUsingFile.propTypes = {};

export default SendUsingFile;
